import {
	PricingTable,
	useAuth,
	useClerk,
	useOrganization,
	useOrganizationList,
} from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import {
	Component,
	useEffect,
	useRef,
	useState,
	type ErrorInfo,
	type ReactNode,
} from "react";
import { WORKING_COPY_FEATURE } from "./billing.js";
import { matrixClerkBillingAppearance } from "./clerk-appearance.js";
import { clerkErrorMessage } from "./clerk-errors.js";
import type { ChatWorkingCopyAsk } from "./messages.js";
import { usePlatformSession } from "./PlatformSession.js";
import {
	readThemePreference,
	resolveTheme,
	type ResolvedTheme,
} from "./theme.js";

type CardProps = {
	ask: ChatWorkingCopyAsk;
	/** Yes hit the paywall — show Clerk plans in this card, not a modal. */
	paying?: boolean;
	onYes: () => void;
	onLater: () => void;
	onPaid?: () => void;
};

export function WorkingCopyAskCard({
	ask,
	paying = false,
	onYes,
	onLater,
	onPaid,
}: CardProps) {
	if (ask.decision === "yes") {
		return (
			<p className="mc-copy-ask-done">Starting your new site…</p>
		);
	}
	if (ask.decision === "later") {
		return (
			<p className="mc-copy-ask-done">
				Whenever you’re ready — we’ll pick this up.
			</p>
		);
	}
	if (paying && onPaid) {
		return (
			<WorkingCopyCheckout
				brandName={ask.brandName}
				onLater={onLater}
				onContinue={onPaid}
			/>
		);
	}
	return (
		<div className="mc-copy-ask" role="group" aria-label="New site">
			<Button type="button" size="sm" className="mc-copy-ask-yes" onClick={onYes}>
				Yes
			</Button>
			<Button
				type="button"
				size="sm"
				variant="ghost"
				className="mc-copy-ask-later"
				onClick={onLater}
			>
				Later
			</Button>
		</div>
	);
}

type BillingErrorState = { message: string | null };

class BillingErrorBoundary extends Component<
	{ children: ReactNode },
	BillingErrorState
> {
	state: BillingErrorState = { message: null };

	static getDerivedStateFromError(error: unknown): BillingErrorState {
		const text = error instanceof Error ? error.message : "Billing unavailable.";
		return { message: text };
	}

	componentDidCatch(error: unknown, info: ErrorInfo): void {
		console.warn("[billing] PricingTable failed", error, info.componentStack);
	}

	render(): ReactNode {
		if (this.state.message) {
			return (
				<p className="mc-paywall-billing-error">
					Clerk Billing isn’t ready on this instance. Enable it in
					Dashboard → Billing, then add an Organization plan with the{" "}
					<code>{WORKING_COPY_FEATURE}</code> feature.
				</p>
			);
		}
		return this.props.children;
	}
}

function firstMembershipOrgId(
	memberships: { organization: { id: string } }[] | undefined,
): string | null {
	const id = memberships?.[0]?.organization.id?.trim();
	return id && id.startsWith("org_") ? id : null;
}

/** Clerk Organization plans — paywall and Settings → Billing. */
export function ClerkOrgPlans({
	open,
	workspaceName,
	resolved,
}: {
	open: boolean;
	workspaceName: string;
	resolved: ResolvedTheme;
}) {
	const { organization, isLoaded: orgLoaded } = useOrganization();
	const { isLoaded: listLoaded, createOrganization, setActive, userMemberships } =
		useOrganizationList({
			userMemberships: { infinite: true },
		});
	const clerk = useClerk();
	const { clerkOrgId, tenantName } = usePlatformSession();
	const [error, setError] = useState<string | null>(null);
	const [retryTick, setRetryTick] = useState(0);
	const appearance = matrixClerkBillingAppearance(resolved);
	const membershipOrgId = firstMembershipOrgId(userMemberships.data);

	useEffect(() => {
		if (!open) {
			setError(null);
			return;
		}
		if (organization) {
			setError(null);
			return;
		}
		if (!orgLoaded || !listLoaded) return;
		const boundId = clerkOrgId?.trim() || null;
		if (!boundId && userMemberships.isLoading) return;

		let cancelled = false;
		const name = workspaceName.trim() || tenantName?.trim() || "Workspace";

		void (async () => {
			try {
				const activate = setActive ?? clerk.setActive.bind(clerk);
				const create = createOrganization;
				const existingId = boundId ?? membershipOrgId;
				if (existingId) {
					await activate({ organization: existingId });
					return;
				}
				if (!create) {
					throw new Error("Couldn’t open checkout for this workspace.");
				}
				const created = await create({ name });
				if (!created) {
					throw new Error("Couldn’t open checkout for this workspace.");
				}
				await activate({ organization: created.id });
			} catch (cause) {
				if (cancelled) return;
				const fallback = membershipOrgId;
				if (fallback) {
					try {
						const activate = setActive ?? clerk.setActive.bind(clerk);
						await activate({ organization: fallback });
						return;
					} catch {
						// fall through to the original error
					}
				}
				setError(
					clerkErrorMessage(cause) ??
						"Couldn’t open checkout for this workspace.",
				);
			}
		})();

		const timeout = window.setTimeout(() => {
			if (!cancelled) {
				setError((current) =>
					current ??
					"Plans are taking too long to open. Try again.",
				);
			}
		}, 15_000);

		return () => {
			cancelled = true;
			window.clearTimeout(timeout);
		};
	}, [
		open,
		orgLoaded,
		listLoaded,
		organization,
		clerkOrgId,
		workspaceName,
		tenantName,
		createOrganization,
		setActive,
		clerk,
		retryTick,
		userMemberships.isLoading,
		membershipOrgId,
	]);

	if (error && !organization) {
		return (
			<div className="mc-paywall-org">
				<p className="mc-paywall-billing-error">{error}</p>
				<Button
					type="button"
					size="sm"
					variant="outline"
					onClick={() => {
						setError(null);
						setRetryTick((n) => n + 1);
					}}
				>
					Try again
				</Button>
			</div>
		);
	}

	if (!organization) {
		return <p className="mc-paywall-loading">Opening checkout…</p>;
	}

	return (
		<BillingErrorBoundary>
			<PricingTable
				for="organization"
				appearance={appearance}
				fallback={
					<p className="mc-paywall-loading">Loading plans…</p>
				}
			/>
		</BillingErrorBoundary>
	);
}

/**
 * Clerk plan table in the chat card. Not a Dialog — Clerk's own checkout
 * drawer must be the only modal, or the yearly toggle and pay sheet sit
 * under our overlay and cannot be used.
 */
export function WorkingCopyCheckout({
	brandName,
	onLater,
	onContinue,
}: {
	brandName: string;
	onLater: () => void;
	onContinue: () => void;
}) {
	const { has, isLoaded } = useAuth();
	const entitled = Boolean(isLoaded && has?.({ feature: WORKING_COPY_FEATURE }));
	const resolved = resolveTheme(readThemePreference());
	const brand = brandName.trim() || "your brand";
	const continued = useRef(false);
	const entitledAtOpen = useRef<boolean | null>(null);

	useEffect(() => {
		if (!isLoaded) return;
		if (entitledAtOpen.current === null) {
			entitledAtOpen.current = entitled;
		}
		if (entitledAtOpen.current || !entitled || continued.current) return;
		continued.current = true;
		onContinue();
	}, [isLoaded, entitled, onContinue]);

	return (
		<div className="mc-copy-pay">
			<p className="mc-copy-pay-lede">
				Pick a plan for {brand}. Use monthly or yearly on the table,
				then Subscribe — payment opens on top of the desk.
			</p>
			<div className="mc-copy-pay-table">
				<ClerkOrgPlans
					open
					workspaceName={brand}
					resolved={resolved}
				/>
			</div>
			<div className="mc-copy-pay-actions">
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="mc-copy-ask-later"
					onClick={onLater}
				>
					Later
				</Button>
			</div>
		</div>
	);
}
